import { useState } from 'react';
import type { EmailGate as GateConfig, Lead } from '../types';
import { NewsletterOptIn } from '../components/NewsletterOptIn';
import { Blocks } from '../components/Blocks';
import { suggestTypoCorrection } from '../lib/email-validator';
import { SmsOptIn } from '../components/SmsOptIn';

interface Props {
  config: GateConfig;
  onSubmit: (lead: Lead) => void;
  /** Set only for sign-ups that offer The Weekly Close box (lib/newsletterOptIn.ts). */
  newsletterSlug?: string | null;
}

const DEFAULT_COUNTRIES = ['Australia', 'Canada', 'United States', 'United Kingdom', 'New Zealand', 'Other'];

export default function EmailGate({ config, onSubmit, newsletterSlug }: Props) {
  // Prefill from URL params (em/fn/ln) — lets a hand-off from another flow
  // (e.g. program registration → welcome survey) carry the lead through so the
  // person doesn't re-type what they just entered. Same fn/ln/em convention the
  // booking page uses. Absent params → empty, i.e. unchanged behavior.
  const [form, setForm] = useState<Lead>(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const em = p.get('em'); const fn = p.get('fn'); const ln = p.get('ln');
      if (em || fn || ln) return { email: em ?? '', firstName: fn ?? undefined, lastName: ln ?? undefined };
    } catch { /* ignore */ }
    return { email: '' };
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const countries = config.countries?.length ? config.countries : DEFAULT_COUNTRIES;

  const has = (f: string) => config.fields.includes(f as any);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (has('firstName') && !form.firstName) e.firstName = 'Required';
    if (has('email')) {
      if (!form.email) e.email = 'Required';
      else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Invalid email';
    }
    if (has('country') && !form.country) e.country = 'Required';
    if (config.consentText && !form.consent) e.consent = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (validate()) onSubmit(form);
  };

  const set = <K extends keyof Lead>(k: K, v: Lead[K]) => setForm({ ...form, [k]: v });

  return (
    <div className="gate-wrapper">
      <div className="card gate-card">
        {config.headline && <h2 className="gate-headline">{config.headline}</h2>}
        {config.subhead && <p className="gate-subhead">{config.subhead}</p>}

        <Blocks blocks={config.blocks} />

        <form onSubmit={handleSubmit} className="gate-form">
          {has('firstName') && (
            <div className="field">
              <label htmlFor="gate-first">First name</label>
              <input
                id="gate-first"
                type="text"
                autoComplete="given-name"
                placeholder="Your first name"
                value={form.firstName ?? ''}
                onChange={(e) => set('firstName', e.target.value)}
              />
              {errors.firstName && <div className="field-error">{errors.firstName}</div>}
            </div>
          )}

          {has('lastName') && (
            <div className="field">
              <label htmlFor="gate-last">Last name</label>
              <input
                id="gate-last"
                type="text"
                autoComplete="family-name"
                placeholder="Your last name"
                value={form.lastName ?? ''}
                onChange={(e) => set('lastName', e.target.value)}
              />
            </div>
          )}

          {has('email') && (
            <div className="field">
              <label htmlFor="gate-email">Email address</label>
              <input
                id="gate-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => {
                  set('email', e.target.value);
                  if (emailSuggestion) setEmailSuggestion(null);
                }}
                onBlur={(e) => {
                  const candidate = suggestTypoCorrection(e.target.value.trim());
                  setEmailSuggestion(
                    candidate && candidate.toLowerCase() !== e.target.value.trim().toLowerCase() ? candidate : null,
                  );
                }}
              />
              {emailSuggestion && (
                <div className="field-suggestion">
                  Did you mean{' '}
                  <button
                    type="button"
                    className="field-suggestion-btn"
                    onClick={() => {
                      set('email', emailSuggestion);
                      setEmailSuggestion(null);
                    }}
                  >
                    {emailSuggestion}
                  </button>
                  ?
                </div>
              )}
              {errors.email && <div className="field-error">{errors.email}</div>}
            </div>
          )}

          {has('country') && (
            <div className="field">
              <label htmlFor="gate-country">Country</label>
              <select id="gate-country" value={form.country ?? ''} onChange={(e) => set('country', e.target.value)}>
                <option value="">Select your country…</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.country && <div className="field-error">{errors.country}</div>}
            </div>
          )}

          {has('phone') && (
            <div className="field">
              <label htmlFor="gate-phone">Phone</label>
              <input
                id="gate-phone"
                type="tel"
                autoComplete="tel"
                placeholder="Optional"
                value={form.phone ?? ''}
                onChange={(e) => set('phone', e.target.value)}
              />
            </div>
          )}

          {has('company') && (
            <div className="field">
              <label htmlFor="gate-company">Business name</label>
              <input
                id="gate-company"
                type="text"
                autoComplete="organization"
                placeholder="Your business"
                value={form.company ?? ''}
                onChange={(e) => set('company', e.target.value)}
              />
            </div>
          )}

          {config.consentText && (
            <label className="consent">
              <input
                type="checkbox"
                checked={!!form.consent}
                onChange={(e) => set('consent', e.target.checked)}
              />
              <span>{config.consentText}</span>
            </label>
          )}
          {errors.consent && <div className="field-error">{errors.consent}</div>}

          <button type="submit" className="btn btn-primary btn-lg gate-submit">
            {config.ctaLabel ?? 'Continue'} →
          </button>
        </form>

        {/* Email-first: once we have a valid email, offer SMS reminders as a
            separate, optional consent. Never gates the primary registration. */}
        {/^\S+@\S+\.\S+$/.test(form.email) && (
          <SmsOptIn
            email={form.email}
            firstName={form.firstName ?? null}
            lastName={form.lastName ?? null}
            source="registration"
          />
        )}
        {newsletterSlug && /^\S+@\S+\.\S+$/.test(form.email) && (
          <NewsletterOptIn
            email={form.email}
            firstName={form.firstName ?? null}
            lastName={form.lastName ?? null}
            source="registration"
            sourceDetail={newsletterSlug}
            page="sign-up"
          />
        )}
      </div>
    </div>
  );
}
